import { useState, useEffect } from "react";
import { Check, Crown, Users, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConsultationForm from "@/components/ConsultationForm";
import { Link } from "react-router-dom";
const AgencyPricing = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const tiers = [{
    name: "Creator Management",
    price: "Custom",
    description: "Agency-level creator management",
    icon: Crown,
    features: ["Full creator oversight", "Roster management", "Booking coordination", "Earnings & payouts", "Analytics reports", "Communications hub", "Profile control", "Multi-creator support"],
    cta: "Contact Sales",
    highlighted: true,
    consultation: true
  }, {
    name: "Venue Oversight",
    price: "Custom",
    description: "Enterprise venue management",
    icon: Users,
    features: ["Multi-venue management", "Advanced analytics", "Team access control", "Finance center", "Gig scheduling", "Ticket management", "Full venue roster", "Priority support"],
    cta: "Contact Sales",
    highlighted: true,
    consultation: true
  }];
  return <div className="min-h-screen bg-black text-white">
      {/* Header - matching /terms style */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        
      </header>

      <main className="container mx-auto px-6 py-12 relative">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 -z-10"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem] -z-10"></div>
        
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-white">
            Enterprise Solutions
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Professional management tools for agencies and enterprises
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {tiers.map(tier => {
          const IconComponent = tier.icon;
          return <Card key={tier.name} className="relative flex flex-col bg-black/60 backdrop-blur-sm border border-white shadow-2xl shadow-white/10 transition-all duration-300 hover:scale-105">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-1 rounded-full text-sm font-semibold">
                  Enterprise
                </div>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 rounded-lg bg-white/10">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl text-white">{tier.name}</CardTitle>
                  </div>
                  <CardDescription className="text-gray-400">{tier.description}</CardDescription>
                  <div className="mt-4">
                    <div className="text-center py-2">
                      <span className="text-2xl font-bold text-white">Custom Pricing</span>
                      <p className="text-sm text-gray-400 mt-1">Contact us for a consultation</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="space-y-3">
                    {tier.features.map(feature => <li key={feature} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full bg-white text-black hover:bg-gray-200 font-semibold" onClick={() => setIsDialogOpen(true)}>
                    {tier.cta}
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>

        {/* Features Grid */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-8">Why Choose Our Enterprise Solutions?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white/5 backdrop-blur-sm border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-lg">Dedicated Support</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">24/7 priority support with dedicated account management</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 backdrop-blur-sm border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-lg">Custom Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">Tailored solutions to fit your existing workflows</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 backdrop-blur-sm border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-lg">Scalable Platform</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">Grow without limits - built for enterprise scale</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="text-center mt-16">
          <p className="text-gray-400 mb-6 text-lg">
            Ready to transform your business operations?
          </p>
          <Button className="bg-white text-black hover:bg-gray-200 font-semibold text-lg px-8 py-6" size="lg" onClick={() => setIsDialogOpen(true)}>
            Start Your Application
          </Button>
        </div>
      </main>

      {/* Registration Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-gray-200 text-black">
          <DialogHeader>
            <DialogTitle className="text-3xl text-gray-900">Business Registration Request</DialogTitle>
            <DialogDescription className="text-gray-600">
              Apply for access to our Enterprise Creator Management or Venue Oversight dashboards
            </DialogDescription>
          </DialogHeader>
          <ConsultationForm lightTheme />
        </DialogContent>
      </Dialog>
    </div>;
};
export default AgencyPricing;